import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Sparkles,
  Activity,
  Target,
  Users,
  Dumbbell,
  ChevronRight,
  Star,
  Award,
  Package,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import ProductCard from '../../components/common/ProductCard';
import TrustStrip from '../../components/common/TrustStrip';
import { products, rehabKits, conditions, therapistRecommendations } from '../../data/mock';

const HomePage = () => {
  const recommendedProducts = products.filter((p) => p.badges?.includes('therapist-recommended'));
  const bestSellers = products.filter((p) => p.badges?.includes('best-seller'));

  const guidedShoppingSteps = [
    { icon: Activity, label: 'Shop by Condition', description: 'Back pain, knee pain, stroke rehab...', href: '/shop?view=condition' },
    { icon: Target, label: 'Shop by Body Part', description: 'Target specific areas', href: '/shop?view=bodypart' },
    { icon: Award, label: 'Therapist Picks', description: 'Expert recommendations', href: '/shop?filter=therapist' },
    { icon: Package, label: 'Rehab Kits', description: 'Complete recovery bundles', href: '/shop?view=kits' },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-white via-red-50/30 to-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-red-100/40 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-64 h-64 bg-rose-100/30 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-24">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100 mb-6">
              <Sparkles className="w-4 h-4 text-red-600" />
              <span className="text-sm font-medium text-gray-600">Clinically-Guided Rehab Shopping</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Shop Rehab Aids —{' '}
              <span className="text-red-600">Guided by Your Recovery</span>
            </h1>

            <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-2xl">
              Expert-curated rehabilitation products matched to your condition, recovery stage,
              and therapist recommendations. Recovery made confident.
            </p>

            <div className="flex flex-wrap gap-3">
              <Link to="/shop">
                <Button size="lg" className="bg-red-600 hover:bg-red-700 text-white rounded-full px-8 h-12 shadow-lg shadow-red-200 hover:shadow-xl hover:shadow-red-300 transition-all">
                  Start Shopping
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/guided-shopping">
                <Button size="lg" variant="outline" className="rounded-full px-8 h-12 border-gray-300 hover:border-red-300 hover:bg-red-50">
                  <Sparkles className="mr-2 w-5 h-5 text-red-600" />
                  Find What's Right for You
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Strip */}
      <TrustStrip />

      {/* Guided Shopping CTAs */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">How Can We Help You Today?</h2>
          <p className="text-gray-600">Find the right products for your recovery journey</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {guidedShoppingSteps.map((step, index) => (
            <Link key={index} to={step.href}>
              <Card className="h-full border-gray-100 hover:border-red-200 hover:shadow-lg transition-all group cursor-pointer">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:bg-red-100 transition-colors">
                    <step.icon className="w-7 h-7 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-1 group-hover:text-red-600 transition-colors">
                    {step.label}
                  </h3>
                  <p className="text-sm text-gray-500">{step.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </section>

      {/* Therapist Recommended */}
      <section className="bg-gradient-to-br from-emerald-50/50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Award className="w-5 h-5 text-emerald-600" />
                <span className="text-sm font-medium text-emerald-600">Expert Picks</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Therapist Recommended</h2>
            </div>
            <Link to="/shop?filter=therapist">
              <Button variant="ghost" className="text-gray-600 hover:text-red-600">
                View All <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {recommendedProducts.slice(0, 4).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Rehab Kits */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Package className="w-5 h-5 text-red-600" />
              <span className="text-sm font-medium text-red-600">Complete Solutions</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Rehab Kits & Bundles</h2>
          </div>
          <Link to="/shop?view=kits">
            <Button variant="ghost" className="text-gray-600 hover:text-red-600">
              View All <ChevronRight className="ml-1 w-4 h-4" />
            </Button>
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {rehabKits.map((kit) => (
            <Card key={kit.id} className="overflow-hidden border-gray-100 hover:border-red-200 hover:shadow-xl transition-all group">
              <div className="aspect-video bg-gradient-to-br from-gray-50 to-gray-100 relative overflow-hidden">
                <img
                  src={kit.image}
                  alt={kit.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute top-3 right-3 bg-red-600 text-white text-sm font-semibold px-3 py-1 rounded-full">
                  Save ${kit.savings.toFixed(2)}
                </div>
              </div>
              <CardContent className="p-5">
                <span className="text-xs font-medium text-red-600 uppercase tracking-wide">
                  {kit.targetStage} Stage
                </span>
                <h3 className="text-lg font-bold text-gray-900 mt-1 mb-2 group-hover:text-red-600 transition-colors">
                  {kit.name}
                </h3>
                <p className="text-sm text-gray-500 mb-4">{kit.description}</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl font-bold text-gray-900">${kit.bundlePrice.toFixed(2)}</span>
                    <span className="text-sm text-gray-400 line-through">${kit.originalPrice.toFixed(2)}</span>
                  </div>
                  <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white rounded-full">
                    Add Kit
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Best Sellers */}
      <section className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between mb-8">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Star className="w-5 h-5 text-amber-500" />
                <span className="text-sm font-medium text-amber-600">Popular Choices</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Best Sellers</h2>
            </div>
            <Link to="/shop?sort=best-seller">
              <Button variant="ghost" className="text-gray-600 hover:text-red-600">
                View All <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {bestSellers.concat(products.slice(0, 4 - bestSellers.length)).slice(0, 4).map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Shop by Condition */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Shop by Condition</h2>
          <p className="text-gray-600">Find products designed for your specific recovery needs</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {conditions.map((condition) => (
            <Link key={condition.id} to={`/shop?condition=${condition.id}`}>
              <div className="p-4 bg-white border border-gray-100 rounded-xl hover:border-red-200 hover:shadow-md transition-all text-center group cursor-pointer">
                <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-red-100 transition-colors">
                  <Activity className="w-5 h-5 text-red-600" />
                </div>
                <span className="text-sm font-medium text-gray-700 group-hover:text-red-600 transition-colors">
                  {condition.name}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-red-600 to-rose-600 py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Not Sure What You Need?
          </h2>
          <p className="text-red-100 mb-8 text-lg">
            Our guided shopping experience helps you find the right products based on your
            condition, goals, and recovery stage.
          </p>
          <Link to="/guided-shopping">
            <Button size="lg" className="bg-white text-red-600 hover:bg-red-50 rounded-full px-8 h-12 shadow-lg">
              <Sparkles className="mr-2 w-5 h-5" />
              Start Guided Shopping
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer Spacer for mobile nav */}
      <div className="h-20 md:h-0" />
    </div>
  );
};

export default HomePage;